import {Expense, SponsorExpense, Balance, Settlement} from '../types';
import {formatDate, formatCurrency} from './helpers';

// Helper function to escape CSV values
const escapeCSV = (value: string | number): string => {
    const str = String(value);
    // If value contains comma, newline, or double quote, wrap in quotes and escape existing quotes
    if (str.includes(',') || str.includes('\n') || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

// Function to export expenses as CSV
export const exportExpensesToCSV = (
    expenses: (Expense | SponsorExpense)[],
    participants: Record<string, string>,
    eventName: string,
    t: (key: string, options?: any) => string
): void => {
    if (expenses.length === 0) {
        alert(t('event.export.noExpenses'));
        return;
    }

    // Sort expenses by timestamp (newest first)
    const sortedExpenses = [...expenses].sort((a, b) => b.timestamp - a.timestamp);

    // CSV Headers
    const headers = [
        t('event.export.headers.date'),
        t('event.export.headers.description'),
        t('event.export.headers.amount'),
        t('event.export.headers.payer'),
        t('event.export.headers.splitBetween'),
        t('event.export.headers.sponsorName'),
        t('event.export.headers.sponsorHolder')
    ];

    // Convert expenses to CSV rows
    const rows = sortedExpenses.map(expense => {
        const date = formatDate(expense.timestamp);
        const description = expense.description || '';
        const amount = Math.abs(expense.amount);

        let payer = '';
        if (expense.type === 'sponsor') {
            payer = t('event.export.values.sponsor');
        } else {
            payer = participants[expense.payer] || expense.payer;
        }

        const splitBetween = expense.splitBetween
            .map(id => participants[id] || id)
            .join('; '); // Use semicolon for list in CSV

        const sponsorName = expense.type === 'sponsor' ? expense.sponsorName || '' : '';
        const sponsorHolder = expense.type === 'sponsor' && expense.sponsorHolder
            ? participants[expense.sponsorHolder] || expense.sponsorHolder
            : '';

        return [
            date,
            description,
            amount,
            payer,
            splitBetween,
            sponsorName,
            sponsorHolder
        ].map(escapeCSV);
    });

    // Combine headers and rows
    const csvContent = [headers.map(escapeCSV), ...rows]
        .map(row => row.join(','))
        .join('\n');

    // Add BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');

    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${eventName}_expenses_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

// Function to export individual balances as CSV
export const exportBalancesToCSV = (
    balances: Balance,
    participants: Record<string, string>,
    eventName: string,
    t: (key: string, options?: any) => string
): void => {
    // CSV Headers
    const headers = [
        t('event.export.balanceHeaders.participant'),
        t('event.export.balanceHeaders.balance'),
        t('event.export.balanceHeaders.status')
    ];

    // Convert balances to CSV rows
    const rows = Object.entries(balances)
        .sort((a, b) => b[1] - a[1]) // Sort by balance descending
        .map(([participantId, balance]) => {
            const name = participants[participantId] || participantId;
            const formattedBalance = formatCurrency(balance);
            const status = balance > 0.01 
                ? t('event.export.balanceStatus.toReceive')
                : balance < -0.01 
                    ? t('event.export.balanceStatus.toPay')
                    : t('event.export.balanceStatus.settled');
            return [name, formattedBalance, status].map(escapeCSV);
        });

    const csvContent = [headers.map(escapeCSV), ...rows]
        .map(row => row.join(','))
        .join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');

    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${eventName}_balances_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

// Function to export suggested payments as CSV
export const exportSettlementsToCSV = (
    settlements: Settlement[],
    participants: Record<string, string>,
    eventName: string,
    t: (key: string, options?: any) => string
): void => {
    if (settlements.length === 0) {
        alert(t('event.export.noSettlements'));
        return;
    }

    // CSV Headers
    const headers = [
        t('event.export.settlementHeaders.from'),
        t('event.export.settlementHeaders.to'),
        t('event.export.settlementHeaders.amount')
    ];

    // Convert settlements to CSV rows
    const rows = settlements.map(settlement => {
        const from = participants[settlement.from] || settlement.from;
        const to = participants[settlement.to] || settlement.to;
        const amount = formatCurrency(settlement.amount);
        return [from, to, amount].map(escapeCSV);
    });

    const csvContent = [headers.map(escapeCSV), ...rows]
        .map(row => row.join(','))
        .join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], {type: 'text/csv;charset=utf-8;'});
    const link = document.createElement('a');

    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${eventName}_settlements_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

// Function to get export summary
export const getExportSummary = (expenses: (Expense | SponsorExpense)[]): {
    totalExpenses: number;
    sponsorExpenses: number;
    totalAmount: number;
    sponsorAmount: number;
} => {
    const regularExpenses = expenses.filter(e => e.type !== 'sponsor');
    const sponsorExpenses = expenses.filter(e => e.type === 'sponsor');

    const totalAmount = regularExpenses.reduce((sum, e) => sum + e.amount, 0);
    const sponsorAmount = sponsorExpenses.reduce((sum, e) => sum + Math.abs(e.amount), 0);

    return {
        totalExpenses: expenses.length,
        sponsorExpenses: sponsorExpenses.length,
        totalAmount,
        sponsorAmount
    };
};